import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  CircularProgress,
  IconButton,
  Typography,
} from '@mui/material';
import {
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon,
  Event as EventIcon,
  Refresh as RefreshIcon,
} from '@mui/icons-material';
import MobileHeader from '../components/MobileHeader';
import api, { handleCatch } from '../api';
import colors from '../common/Colors';

const ACADEMIC_MONTHS = [3, 4, 5, 6, 7, 8, 9, 10, 11, 0, 1, 2];
const MONTH_NAMES = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
];
const WEEKDAY_NAMES = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const CALENDAR_PRIMARY = colors.BLUE;
const CALENDAR_ACCENT = colors.ORANGE;

const parseDateOnly = (value) => {
  if (!value) return null;
  const [datePart] = String(value).split('T');
  const [year, month, day] = datePart.split('-').map(Number);
  if (!year || !month || !day) return null;
  return new Date(year, month - 1, day);
};

const getAcademicYearStart = () => {
  const now = new Date();
  return now.getMonth() >= 3 ? now.getFullYear() : now.getFullYear() - 1;
};

const formatHolidayDate = (date) => {
  if (!date) return '';
  const day = String(date.getDate()).padStart(2, '0');
  return `${day} ${MONTH_NAMES[date.getMonth()].slice(0, 3)}, ${date.getFullYear()}, ${WEEKDAY_NAMES[date.getDay()]}`;
};

const normalizeHoliday = (holiday) => {
  const date = parseDateOnly(holiday.holidayDate);
  return {
    ...holiday,
    parsedDate: date,
    monthIndex: date ? date.getMonth() : -1,
    displayDate: formatHolidayDate(date),
  };
};

const HolidayCalendarScreen = () => {
  const [academicYear, setAcademicYear] = useState(getAcademicYearStart());
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth());
  const [holidays, setHolidays] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    loadHolidays();
  }, [academicYear]);

  const loadHolidays = async () => {
    setLoading(true);
    setError('');
    try {
      const [currentYearRes, nextYearRes] = await Promise.all([
        api.get(`api/school/school-holidays/student-calendar?year=${academicYear}`),
        api.get(`api/school/school-holidays/student-calendar?year=${academicYear + 1}`),
      ]);

      const normalized = [...(currentYearRes.data || []), ...(nextYearRes.data || [])]
        .map(normalizeHoliday)
        .filter((holiday) => {
          if (!holiday.parsedDate) return false;
          const year = holiday.parsedDate.getFullYear();
          const month = holiday.parsedDate.getMonth();
          return (year === academicYear && month >= 3) || (year === academicYear + 1 && month <= 2);
        })
        .sort((a, b) => a.parsedDate - b.parsedDate);

      setHolidays(normalized);

      const currentMonth = new Date().getMonth();
      const hasCurrentMonth = normalized.some((holiday) => holiday.monthIndex === currentMonth);
      const firstHolidayMonth = normalized[0]?.monthIndex;
      setSelectedMonth(hasCurrentMonth ? currentMonth : (firstHolidayMonth ?? 3));
    } catch (err) {
      console.error('HolidayCalendarScreen loadHolidays error:', err);
      setError(handleCatch(err) || 'Failed to load holiday calendar');
      setHolidays([]);
    } finally {
      setLoading(false);
    }
  };

  const selectedMonthHolidays = useMemo(
    () => holidays.filter((holiday) => holiday.monthIndex === selectedMonth),
    [holidays, selectedMonth]
  );

  const holidayMonthSet = useMemo(
    () => new Set(holidays.map((holiday) => holiday.monthIndex)),
    [holidays]
  );

  const changeYear = (delta) => {
    setAcademicYear((prev) => prev + delta);
  };

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: '#F5F7FA', display: 'flex', flexDirection: 'column' }}>
      <MobileHeader title="Calendar" backgroundColor={CALENDAR_PRIMARY} />

      <Box sx={{ flex: 1, overflowY: 'auto', pb: 8 }}>
        <Box sx={{ px: 2.5, pt: 3, pb: 1.5 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <IconButton size="small" onClick={() => changeYear(-1)} sx={{ color: CALENDAR_PRIMARY }}>
              <ChevronLeftIcon />
            </IconButton>
            <Typography
              sx={{
                color: CALENDAR_PRIMARY,
                fontWeight: 800,
                fontSize: 26,
                letterSpacing: 0.5,
                lineHeight: 1,
              }}
            >
              {academicYear}-{academicYear + 1}
            </Typography>
            <Box sx={{ width: 40, height: 2, bgcolor: CALENDAR_ACCENT, ml: 0.5 }} />
            <IconButton size="small" onClick={() => changeYear(1)} sx={{ color: CALENDAR_PRIMARY }}>
              <ChevronRightIcon />
            </IconButton>
            <IconButton
              size="small"
              onClick={loadHolidays}
              disabled={loading}
              aria-label="Reload holidays"
              sx={{
                color: CALENDAR_PRIMARY,
                ml: 'auto',
                border: `1px solid ${CALENDAR_PRIMARY}`,
                bgcolor: '#fff',
                '&:disabled': { color: '#9E9E9E', borderColor: '#D0D0D0' },
              }}
            >
              <RefreshIcon fontSize="small" />
            </IconButton>
          </Box>
        </Box>

        {loading ? (
          <Box sx={{ py: 8, display: 'flex', justifyContent: 'center' }}>
            <CircularProgress sx={{ color: CALENDAR_PRIMARY }} />
          </Box>
        ) : error ? (
          <Box sx={{ px: 2, pt: 2 }}>
            <Alert severity="error">{error}</Alert>
          </Box>
        ) : selectedMonthHolidays.length === 0 ? (
          <Box sx={{ px: 2, pt: 6, textAlign: 'center' }}>
            <EventIcon sx={{ fontSize: 48, color: '#D9DDF9', mb: 1 }} />
            <Typography sx={{ fontWeight: 700, color: CALENDAR_PRIMARY, mb: 0.5 }}>
              No holidays in {MONTH_NAMES[selectedMonth]}
            </Typography>
            <Typography sx={{ color: '#777', fontSize: 13 }}>
              Select another month to view holiday details.
            </Typography>
          </Box>
        ) : (
          <Box sx={{ position: 'relative', px: 1.5, pt: 1 }}>
            <Box
              sx={{
                position: 'absolute',
                top: 6,
                bottom: 0,
                left: 'calc(43% + 21px)',
                width: 2,
                bgcolor: CALENDAR_PRIMARY,
                opacity: 0.9,
              }}
            />

            {selectedMonthHolidays.map((holiday) => (
              <Box
                key={holiday.id}
                sx={{
                  display: 'grid',
                  gridTemplateColumns: '43% 44px 1fr',
                  alignItems: 'center',
                  minHeight: 116,
                  position: 'relative',
                }}
              >
                <Typography
                  sx={{
                    color: CALENDAR_PRIMARY,
                    fontWeight: 500,
                    fontSize: 16,
                    pr: 1,
                    whiteSpace: 'nowrap',
                  }}
                >
                  {holiday.displayDate}
                </Typography>

                <Box sx={{ display: 'flex', justifyContent: 'center', zIndex: 1 }}>
                  <Box
                    sx={{
                      width: 30,
                      height: 30,
                      borderRadius: '50%',
                      bgcolor: '#fff',
                      border: `2px solid ${CALENDAR_PRIMARY}`,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    <Box sx={{ width: 18, height: 18, borderRadius: '50%', bgcolor: CALENDAR_ACCENT }} />
                  </Box>
                </Box>

                <Box
                  sx={{
                    position: 'relative',
                    bgcolor: CALENDAR_PRIMARY,
                    color: '#fff',
                    px: 1.5,
                    py: 1.1,
                    minHeight: 46,
                    display: 'flex',
                    alignItems: 'center',
                    fontWeight: 700,
                    fontSize: 14,
                    lineHeight: 1.25,
                    textTransform: 'uppercase',
                    '&::before': {
                      content: '""',
                      position: 'absolute',
                      left: -10,
                      top: '50%',
                      transform: 'translateY(-50%)',
                      borderTop: '10px solid transparent',
                      borderBottom: '10px solid transparent',
                      borderRight: `10px solid ${CALENDAR_PRIMARY}`,
                    },
                  }}
                >
                  {holiday.name}
                </Box>
              </Box>
            ))}
          </Box>
        )}
      </Box>

      <Box
        sx={{
          position: 'fixed',
          left: 0,
          right: 0,
          bottom: 0,
          bgcolor: '#fff',
          borderTop: '1px solid #e0e0e0',
          display: 'flex',
          overflowX: 'auto',
          zIndex: 1000,
          scrollbarWidth: 'none',
          '&::-webkit-scrollbar': { display: 'none' },
        }}
      >
        {ACADEMIC_MONTHS.map((monthIndex) => {
          const active = selectedMonth === monthIndex;
          const hasHoliday = holidayMonthSet.has(monthIndex);
          return (
            <Box
              key={monthIndex}
              onClick={() => setSelectedMonth(monthIndex)}
              sx={{
                minWidth: 92,
                px: 1.5,
                py: 1.4,
                textAlign: 'center',
                cursor: 'pointer',
                color: active ? CALENDAR_PRIMARY : '#333',
                fontWeight: active ? 800 : 700,
                position: 'relative',
                WebkitTapHighlightColor: 'transparent',
                '&::after': active
                  ? {
                      content: '""',
                      position: 'absolute',
                      left: 16,
                      right: 16,
                      bottom: 0,
                      height: 4,
                      borderRadius: '4px 4px 0 0',
                      bgcolor: CALENDAR_ACCENT,
                    }
                  : {},
              }}
            >
              <Typography sx={{ fontSize: 16, fontWeight: 'inherit' }}>
                {MONTH_NAMES[monthIndex]}
              </Typography>
              {hasHoliday && !active && (
                <Box
                  sx={{
                    width: 5,
                    height: 5,
                    borderRadius: '50%',
                    bgcolor: CALENDAR_ACCENT,
                    mx: 'auto',
                    mt: 0.4,
                  }}
                />
              )}
            </Box>
          );
        })}
      </Box>
    </Box>
  );
};

export default HolidayCalendarScreen;
